import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import db from '../../firebaseConfig';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import usePageTitle from '../../hooks/usePageTitle';
import { getScopedSettingsDocId, useTianlanStore, withShopScope } from '../../utils/tianlanStore';
import {
  DEFAULT_TIANLAN_CUSTOMER_THEME,
  normalizeTianlanCustomerTheme,
} from '../../utils/tianlanCustomerTheme';
import '../../styles.css';
import '../../tianlan-modern.css';

const DEFAULT_EXCHANGE_SMS_TEMPLATE = '天藍小舖直播通知-您的訂單{{PA單號}}\n您好，您的訂單有{{需換貨數量}}件商品缺貨，{{換貨期限文字}}：{{換貨網址}}\n如有問題請私訊天藍小舖粉專客服，謝謝您。';
const DEFAULT_FORM_SMS_TEMPLATE = '天藍小舖直播通知-您的訂單{{PA單號}}\n您好，您的訂單有{{需填表單數量}}項售後資料需要填寫，{{表單期限文字}}：{{表單網址}}\n如有問題請私訊天藍小舖粉專客服，謝謝您。';

const SMS_TEMPLATE_VARIABLES = [
  { label: 'PA單號', token: '{{PA單號}}' },
  { label: '客人姓名', token: '{{客人姓名}}' },
  { label: '電話', token: '{{電話}}' },
  { label: '需換貨數量', token: '{{需換貨數量}}' },
  { label: '換貨期限文字', token: '{{換貨期限文字}}' },
  { label: '換貨網址', token: '{{換貨網址}}' },
  { label: '缺貨商品摘要', token: '{{缺貨商品摘要}}' },
];

const FORM_SMS_TEMPLATE_VARIABLES = [
  { label: 'PA單號', token: '{{PA單號}}' },
  { label: '客人姓名', token: '{{客人姓名}}' },
  { label: '電話', token: '{{電話}}' },
  { label: '需填表單數量', token: '{{需填表單數量}}' },
  { label: '表單期限文字', token: '{{表單期限文字}}' },
  { label: '表單網址', token: '{{表單網址}}' },
  { label: '售後商品摘要', token: '{{售後商品摘要}}' },
  { label: '表單名稱', token: '{{表單名稱}}' },
];

const BRAND_LOGO_URL = 'https://cms-static.cdn.91app.com/images/original/11/20907f91-ac0f-46b4-82b1-0aebda39ccbc-1632897043-6ggal54opq_d.png';

function TianlanSettings() {
  usePageTitle('系統設定', '天藍小舖後台', '設定換貨系統參數和訊息');
  const currentShop = useTianlanStore();

  const defaultSettings = useMemo(() => ({
    completedMessage: '您的訂單換貨已完成，目前無可換貨商品。',
    completedThankYou: '感謝您使用天藍小舖直播換貨服務！',
    expiredMessage: '此PA單號的換貨時間已過期，無法進行換貨操作。',
    expiredThankYou: '如有疑問請聯繫客服人員！',
    maintenanceMode: false,
    maintenanceMessage: '系統目前進行維護中，暫時無法提供服務。',
    maintenanceTitle: '系統維護通知',
    jamboExchangeEnabled: true,
    jamboAutoRun: false,
    jamboTemporaryPricePatch: true,
    exchangeSmsTemplate: DEFAULT_EXCHANGE_SMS_TEMPLATE,
    formSmsTemplate: DEFAULT_FORM_SMS_TEMPLATE,
    smsAccessLinkDays: 7
  }), []);

  const defaultAnnouncement = useMemo(() =>
    '📣如果有任何問題歡迎詢問「天藍小舖粉絲團」或是添加官方LINE ID「@skybluetw」詢問，感謝您的配合～'
  , []);

  const defaultAnnouncementStyle = useMemo(() => ({
    backgroundColor: '#f7c873',
    textColor: '#2c3e50'
  }), []);

  const defaultCustomerTheme = useMemo(() => ({ ...DEFAULT_TIANLAN_CUSTOMER_THEME }), []);

  const [settings, setSettings] = useState(defaultSettings);
  const [announcement, setAnnouncement] = useState(defaultAnnouncement);
  const [announcementStyle, setAnnouncementStyle] = useState(defaultAnnouncementStyle);
  const [customerTheme, setCustomerTheme] = useState(defaultCustomerTheme);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const smsTemplateRef = useRef(null);
  const formSmsTemplateRef = useRef(null);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const [settingsDoc, announcementDoc, announcementStyleDoc, customerThemeDoc] = await Promise.all([
        getDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('messages', currentShop))),
        getDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('announcement', currentShop))),
        getDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('announcement_style', currentShop))),
        getDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('customer_theme', currentShop)))
      ]);

      if (settingsDoc.exists()) {
        setSettings({ ...defaultSettings, ...settingsDoc.data() });
      }

      if (announcementDoc.exists()) {
        setAnnouncement(announcementDoc.data().content);
      }

      if (announcementStyleDoc.exists()) {
        setAnnouncementStyle({ ...defaultAnnouncementStyle, ...announcementStyleDoc.data() });
      }

      if (customerThemeDoc.exists()) {
        setCustomerTheme(normalizeTianlanCustomerTheme(customerThemeDoc.data()));
      }
    } catch (error) {
      console.error('載入設定失敗:', error);
      setNotice({ tone: 'error', title: '載入設定失敗', message: error.message || '請稍後再試' });
    }
    setIsLoading(false);
  }, [defaultSettings, defaultAnnouncementStyle, defaultCustomerTheme, currentShop]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomerThemeChange = (field, value) => {
    setCustomerTheme(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      await Promise.all([
        setDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('messages', currentShop)), withShopScope(settings, currentShop)),
        setDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('announcement', currentShop)), withShopScope({ content: announcement }, currentShop)),
        setDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('announcement_style', currentShop)), withShopScope(announcementStyle, currentShop)),
        setDoc(doc(db, 'tianlan_settings', getScopedSettingsDocId('customer_theme', currentShop)), withShopScope(customerTheme, currentShop))
      ]);
      setNotice({ tone: 'success', title: '設定已儲存', message: '客人換貨頁與後台流程會使用最新設定。' });
    } catch (error) {
      console.error('儲存設定失敗:', error);
      setNotice({ tone: 'error', title: '儲存失敗', message: '請稍後再試，或確認網路連線。' });
    }
    setIsSaving(false);
  };

  const resetToDefault = useCallback(() => {
    setSettings(defaultSettings);
    setAnnouncement(defaultAnnouncement);
    setAnnouncementStyle(defaultAnnouncementStyle);
    setCustomerTheme(defaultCustomerTheme);
    setShowResetConfirm(false);
    setNotice({ tone: 'success', title: '已恢復預設內容', message: '確認內容後請記得儲存設定。' });
  }, [defaultSettings, defaultAnnouncement, defaultAnnouncementStyle, defaultCustomerTheme]);

  const insertSmsTemplateVariable = (token, field = 'exchangeSmsTemplate', ref = smsTemplateRef) => {
    const textarea = ref.current;
    const current = settings[field] || '';
    if (!textarea || document.activeElement !== textarea) {
      handleInputChange(field, `${current}${token}`);
      return;
    }
    const start = textarea.selectionStart ?? current.length;
    const end = textarea.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
    handleInputChange(field, next);
    window.requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const exchangeModeLabel = settings.jamboAutoRun ? '自動換貨' : '人工確認';
  const storeLabel = currentShop?.storeName || currentShop?.name || currentShop?.shopName || '天藍小舖';
  const shopIdLabel = currentShop?.shopUserId || currentShop?.storeId || currentShop?.id || '目前商店';

  const renderHeader = () => (
    <div className="modern-page-header tl-settings-header">
      <div className="modern-container">
        <Link to="/exchange/tianlan" className="modern-back-btn tl-settings-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m15 18-6-6 6-6"/>
          </svg>
          返回首頁
        </Link>
        <div className="tl-settings-hero">
          <div>
            <div className="tl-settings-kicker">Skyblue live exchange</div>
            <h1>系統設定</h1>
            <p>調整客人換貨頁顯示文字、查詢公告、簡訊文案與自動換貨參數。</p>
          </div>
          <div className="tl-settings-meta">
            <span>{storeLabel}</span>
            <span>{shopIdLabel}</span>
            <span>{exchangeModeLabel}</span>
            {settings.maintenanceMode && <span className="warning">維護中</span>}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCustomerThemeCard = () => (
    <div className="modern-card tl-settings-card tl-settings-theme-card">
      <div className="modern-card-header">
        <div className="modern-card-title">客人介面樣式</div>
        <div className="modern-card-description">調整客人查詢頁、平板子母畫面底色與主要按鈕顏色。iPad／平板子母畫面底色預設為黑色。</div>
      </div>

      <div className="tl-settings-color-grid">
        <div className="modern-form-group">
          <label className="modern-form-label">頁面底色</label>
          <div className="tl-settings-color-input">
            <input
              type="color"
              value={customerTheme.pageBackgroundColor}
              onChange={(e) => handleCustomerThemeChange('pageBackgroundColor', e.target.value)}
            />
            <input
              type="text"
              value={customerTheme.pageBackgroundColor}
              onChange={(e) => handleCustomerThemeChange('pageBackgroundColor', e.target.value)}
              className="modern-input"
              placeholder="#f7f5f0"
            />
          </div>
        </div>

        <div className="modern-form-group">
          <label className="modern-form-label">卡片底色</label>
          <div className="tl-settings-color-input">
            <input
              type="color"
              value={customerTheme.cardBackgroundColor}
              onChange={(e) => handleCustomerThemeChange('cardBackgroundColor', e.target.value)}
            />
            <input
              type="text"
              value={customerTheme.cardBackgroundColor}
              onChange={(e) => handleCustomerThemeChange('cardBackgroundColor', e.target.value)}
              className="modern-input"
              placeholder="#fffdf8"
            />
          </div>
        </div>

        <div className="modern-form-group">
          <label className="modern-form-label">iPad／平板子母畫面底色</label>
          <div className="tl-settings-color-input">
            <input
              type="color"
              value={customerTheme.tabletFrameBackgroundColor}
              onChange={(e) => handleCustomerThemeChange('tabletFrameBackgroundColor', e.target.value)}
            />
            <input
              type="text"
              value={customerTheme.tabletFrameBackgroundColor}
              onChange={(e) => handleCustomerThemeChange('tabletFrameBackgroundColor', e.target.value)}
              className="modern-input"
              placeholder="#000000"
            />
          </div>
        </div>

        <div className="modern-form-group">
          <label className="modern-form-label">主按鈕顏色</label>
          <div className="tl-settings-color-input">
            <input
              type="color"
              value={customerTheme.buttonColor}
              onChange={(e) => handleCustomerThemeChange('buttonColor', e.target.value)}
            />
            <input
              type="text"
              value={customerTheme.buttonColor}
              onChange={(e) => handleCustomerThemeChange('buttonColor', e.target.value)}
              className="modern-input"
              placeholder="#1f7895"
            />
          </div>
        </div>

        <div className="modern-form-group">
          <label className="modern-form-label">重點色</label>
          <div className="tl-settings-color-input">
            <input
              type="color"
              value={customerTheme.accentColor}
              onChange={(e) => handleCustomerThemeChange('accentColor', e.target.value)}
            />
            <input
              type="text"
              value={customerTheme.accentColor}
              onChange={(e) => handleCustomerThemeChange('accentColor', e.target.value)}
              className="modern-input"
              placeholder="#1f7895"
            />
          </div>
        </div>
      </div>

      <button
        type="button"
        className="modern-btn modern-btn-secondary"
        onClick={() => {
          setCustomerTheme(defaultCustomerTheme);
          setNotice({ tone: 'success', title: '已套用預設介面色', message: 'iPad／平板子母畫面底色已恢復黑色，確認後請儲存設定。' });
        }}
      >
        恢復預設介面色
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="modern-page tl-settings-page">
        {renderHeader()}
        <div className="modern-container">
          <div className="modern-loading">
            <div className="modern-spinner"></div>
            載入中...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-page tl-settings-page">
      {renderHeader()}

      <div className="modern-container">
        {notice && (
          <div className={`tl-settings-notice ${notice.tone || 'info'}`}>
            <div>
              <strong>{notice.title}</strong>
              <span>{notice.message}</span>
            </div>
            <button type="button" onClick={() => setNotice(null)}>關閉</button>
          </div>
        )}

        <div className="tl-settings-layout">
          <div className="tl-settings-form-stack">
            {renderCustomerThemeCard()}

            <div className="modern-card tl-settings-card">
              <div className="modern-card-header">
                <div className="modern-card-title">查無待選項目畫面</div>
                <div className="modern-card-description">客人查詢到已送出、已處理，或目前沒有需要選擇的換貨項目時顯示。</div>
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">主要訊息</label>
                <textarea
                  value={settings.completedMessage}
                  onChange={(e) => handleInputChange('completedMessage', e.target.value)}
                  className="modern-input"
                  style={{ minHeight: '80px' }}
                  placeholder="輸入沒有待選項目時顯示的主要訊息"
                />
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">補充說明</label>
                <textarea
                  value={settings.completedThankYou}
                  onChange={(e) => handleInputChange('completedThankYou', e.target.value)}
                  className="modern-input"
                  style={{ minHeight: '60px' }}
                  placeholder="輸入補充說明或客服提醒"
                />
              </div>
            </div>

            <div className="modern-card tl-settings-card">
              <div className="modern-card-header">
                <div className="modern-card-title">換貨截止畫面</div>
                <div className="modern-card-description">付款單已超過換貨期限時，客人端不會顯示商品選擇，只顯示這裡的說明。</div>
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">主要訊息</label>
                <textarea
                  value={settings.expiredMessage}
                  onChange={(e) => handleInputChange('expiredMessage', e.target.value)}
                  className="modern-input"
                  style={{ minHeight: '80px' }}
                  placeholder="輸入過期時顯示的主要訊息"
                />
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">提示文字</label>
                <textarea
                  value={settings.expiredThankYou}
                  onChange={(e) => handleInputChange('expiredThankYou', e.target.value)}
                  className="modern-input"
                  style={{ minHeight: '60px' }}
                  placeholder="輸入過期時顯示的提示文字"
                />
              </div>
            </div>

            <div className="modern-card tl-settings-card">
              <div className="modern-card-header">
                <div className="modern-card-title" id="sms-access-link" style={{ scrollMarginTop: 100 }}>簡訊安全連結效期</div>
                <div className="modern-card-description">簡訊連結可查看同商店、同收件電話的待處理訂單。此處設定預設效期，也可在發送簡訊時單獨選擇。</div>
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">連結有效期限</label>
                <select
                  value={settings.smsAccessLinkDays}
                  onChange={(e) => handleInputChange('smsAccessLinkDays', Number(e.target.value))}
                  className="modern-input"
                >
                  <option value={1}>1 天</option>
                  <option value={3}>3 天</option>
                  <option value={7}>7 天（預設）</option>
                  <option value={14}>14 天</option>
                  <option value={30}>30 天</option>
                </select>
              </div>
            </div>

            <div className="modern-card tl-settings-card">
              <div className="modern-card-header">
                <div className="modern-card-title">換貨簡訊文案模組</div>
                <div className="modern-card-description">後台多選付款單產生簡訊時使用，變數會依每張付款單自動替換。</div>
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">簡訊內容</label>
                <textarea
                  ref={smsTemplateRef}
                  value={settings.exchangeSmsTemplate}
                  onChange={(e) => handleInputChange('exchangeSmsTemplate', e.target.value)}
                  className="modern-input"
                  style={{ minHeight: '110px' }}
                  placeholder="輸入換貨簡訊模板"
                />
              </div>
              <div className="tl-variable-chip-row">
                {SMS_TEMPLATE_VARIABLES.map((item) => (
                  <button
                    type="button"
                    key={item.token}
                    className="tl-style-chip"
                    onClick={() => insertSmsTemplateVariable(item.token)}
                  >
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
              <div className="modern-card-description">
                舊變數仍支援：{'{{paNumber}}'}、{'{{customerName}}'}、{'{{phone}}'}、{'{{deadline}}'}、{'{{exchangeUrl}}'}、{'{{itemCount}}'}、{'{{productSummary}}'}
              </div>
            </div>

            <div className="modern-card tl-settings-card">
              <div className="modern-card-header">
                <div className="modern-card-title">表單簡訊文案模組</div>
                <div className="modern-card-description">後台多選售後表單任務發送簡訊時使用，同商店、同收件電話的多張付款單會自動合併成一封。</div>
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">簡訊內容</label>
                <textarea
                  ref={formSmsTemplateRef}
                  value={settings.formSmsTemplate}
                  onChange={(e) => handleInputChange('formSmsTemplate', e.target.value)}
                  className="modern-input"
                  style={{ minHeight: '110px' }}
                  placeholder="輸入表單簡訊模板"
                />
              </div>
              <div className="tl-variable-chip-row">
                {FORM_SMS_TEMPLATE_VARIABLES.map((item) => (
                  <button
                    type="button"
                    key={item.token}
                    className="tl-style-chip"
                    onClick={() => insertSmsTemplateVariable(item.token, 'formSmsTemplate', formSmsTemplateRef)}
                  >
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
              <div className="modern-card-description">
                表單簡訊也可沿用：{'{{paNumber}}'}、{'{{customerName}}'}、{'{{phone}}'}、{'{{deadlineText}}'}、{'{{serviceUrl}}'}、{'{{formItemCount}}'}、{'{{productSummary}}'}
              </div>
            </div>

            <div className="modern-card tl-settings-card">
              <div className="modern-card-header">
                <div className="modern-card-title">客人查詢公告</div>
                <div className="modern-card-description">顯示在客人換貨查詢頁，適合放客服聯絡方式或查詢提醒。</div>
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">公告內容</label>
                <textarea
                  value={announcement}
                  onChange={(e) => setAnnouncement(e.target.value)}
                  className="modern-input"
                  style={{ minHeight: '100px' }}
                  placeholder="輸入公告內容"
                />
                <div className="tl-settings-field-note">
                  會顯示在「查詢前請確認」下方，適合放客服提醒或查詢注意事項。
                </div>
              </div>

              <div className="tl-settings-color-grid">
                <div className="modern-form-group">
                  <label className="modern-form-label">公告背景</label>
                  <div className="tl-settings-color-input">
                    <input
                      type="color"
                      value={announcementStyle.backgroundColor}
                      onChange={(e) => setAnnouncementStyle(prev => ({
                        ...prev,
                        backgroundColor: e.target.value
                      }))}
                    />
                    <input
                      type="text"
                      value={announcementStyle.backgroundColor}
                      onChange={(e) => setAnnouncementStyle(prev => ({
                        ...prev,
                        backgroundColor: e.target.value
                      }))}
                      className="modern-input"
                      placeholder="#f7c873"
                    />
                  </div>
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">公告文字</label>
                  <div className="tl-settings-color-input">
                    <input
                      type="color"
                      value={announcementStyle.textColor}
                      onChange={(e) => setAnnouncementStyle(prev => ({
                        ...prev,
                        textColor: e.target.value
                      }))}
                    />
                    <input
                      type="text"
                      value={announcementStyle.textColor}
                      onChange={(e) => setAnnouncementStyle(prev => ({
                        ...prev,
                        textColor: e.target.value
                      }))}
                      className="modern-input"
                      placeholder="#2c3e50"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modern-card tl-settings-card">
              <div className="modern-card-header">
                <div className="modern-card-title">換貨處理設定</div>
                <div className="modern-card-description">設定客人送出後要進入人工確認，或直接排隊自動執行換貨。</div>
              </div>

              <label className="tl-settings-check-row">
                <input
                  type="checkbox"
                  checked={settings.jamboExchangeEnabled}
                  onChange={(e) => handleInputChange('jamboExchangeEnabled', e.target.checked)}
                />
                <span>
                  <strong>啟用直播訂單換貨</strong>
                  <em>關閉後客人仍可查詢說明，但不會進行直播訂單換貨流程。</em>
                </span>
              </label>

              <div className="tl-settings-radio-group">
                <label className={settings.jamboAutoRun ? '' : 'selected'}>
                  <input
                    type="radio"
                    name="jamboAutoRun"
                    checked={!settings.jamboAutoRun}
                    onChange={() => handleInputChange('jamboAutoRun', false)}
                  />
                  <span>
                    <strong>人工確認</strong>
                    <em>客人送出後先進入換貨處理清單，由後台人員逐筆確認。</em>
                  </span>
                </label>
                <label className={settings.jamboAutoRun ? 'selected' : ''}>
                  <input
                    type="radio"
                    name="jamboAutoRun"
                    checked={settings.jamboAutoRun}
                    onChange={() => handleInputChange('jamboAutoRun', true)}
                  />
                  <span>
                    <strong>自動換貨</strong>
                    <em>客人送出後加入佇列自動處理，仍保留商品鎖與價格還原。</em>
                  </span>
                </label>
              </div>

              <label className="tl-settings-check-row">
                <input
                  type="checkbox"
                  checked={settings.jamboTemporaryPricePatch}
                  onChange={(e) => handleInputChange('jamboTemporaryPricePatch', e.target.checked)}
                />
                <span>
                  <strong>換貨前暫改直播價</strong>
                  <em>換貨完成或失敗後會嘗試還原原本價格。</em>
                </span>
              </label>
            </div>

            <div className="modern-card tl-settings-card">
              <div className="modern-card-header">
                <div className="modern-card-title">系統維護模式</div>
                <div className="modern-card-description">開啟時會關閉客人查詢功能，並顯示維護通知。</div>
              </div>

              <label className="tl-settings-check-row">
                <input
                  type="checkbox"
                  checked={settings.maintenanceMode}
                  onChange={(e) => handleInputChange('maintenanceMode', e.target.checked)}
                />
                <span>
                  <strong>啟用維護模式</strong>
                  <em>{settings.maintenanceMode ? '客人目前無法使用搜尋功能。' : '啟用後客人會看到維護通知。'}</em>
                </span>
              </label>

              <div className="modern-form-group">
                <label className="modern-form-label">維護通知標題</label>
                <input
                  type="text"
                  value={settings.maintenanceTitle}
                  onChange={(e) => handleInputChange('maintenanceTitle', e.target.value)}
                  className="modern-input"
                  placeholder="輸入維護通知的標題"
                />
              </div>

              <div className="modern-form-group">
                <label className="modern-form-label">維護通知內容</label>
                <textarea
                  value={settings.maintenanceMessage}
                  onChange={(e) => handleInputChange('maintenanceMessage', e.target.value)}
                  className="modern-input"
                  style={{ minHeight: '80px' }}
                  placeholder="輸入維護期間顯示給客人的訊息"
                />
              </div>
            </div>

            <div className="tl-settings-actions">
              <button type="button" onClick={() => setShowResetConfirm(true)} className="modern-btn modern-btn-secondary">
                恢復預設
              </button>
              <button type="button" onClick={saveSettings} disabled={isSaving} className="modern-btn modern-btn-primary">
                {isSaving ? '儲存中...' : '儲存設定'}
              </button>
            </div>
          </div>

          <div className="tl-settings-preview-column">
            <div className="modern-card tl-settings-preview-card">
              <div className="modern-card-header">
                <div className="modern-card-title">客人換貨頁預覽</div>
                <div className="modern-card-description">版型貼近目前前台搜尋與換貨頁，方便確認文字是否自然。</div>
              </div>

              <div
                className="tl-settings-customer-preview"
                style={{
                  '--tl-customer-page-bg': customerTheme.pageBackgroundColor,
                  '--tl-customer-card-bg': customerTheme.cardBackgroundColor,
                  '--tl-customer-tablet-bg': customerTheme.tabletFrameBackgroundColor,
                  '--tl-customer-button-bg': customerTheme.buttonColor,
                  '--tl-customer-accent': customerTheme.accentColor
                }}
              >
                <div className="tl-settings-preview-frame-chip">
                  <span>iPad／平板子母底色</span>
                  <strong>{customerTheme.tabletFrameBackgroundColor}</strong>
                </div>
                <div className="tl-settings-preview-hero">
                  <div className="tl-settings-preview-brand">
                    <img src={BRAND_LOGO_URL} alt="天藍小舖" />
                    <div>
                      <span>Skyblue live exchange</span>
                      <strong>天藍小舖直播換貨系統</strong>
                    </div>
                  </div>
                  <p>輸入付款單 PA 單號或收件人電話，系統會自動查詢可換貨資料。</p>
                </div>

                <div className="tl-settings-preview-search">
                  <div className="tl-settings-preview-row">
                    <div>
                      <span>查詢換貨資料</span>
                      <p>請輸入付款單 PA 單號或收件人手機</p>
                    </div>
                    <em>等待輸入</em>
                  </div>
                  <div className="tl-settings-preview-input">
                    <span>PA / Phone</span>
                    <strong>輸入 PA 單號或收件人電話</strong>
                    <button type="button">查詢訂單</button>
                  </div>
                </div>

                <div
                  className="tl-settings-preview-banner"
                  style={{
                    backgroundColor: announcementStyle.backgroundColor,
                    color: announcementStyle.textColor
                  }}
                >
                  {announcement}
                </div>

                <div className="tl-settings-preview-summary">
                  <div>
                    <span>查詢結果</span>
                    <strong>1 張付款單</strong>
                  </div>
                  <div>
                    <span>待選項目</span>
                    <strong>0 項</strong>
                  </div>
                  <div>
                    <span>目前狀態</span>
                    <strong>{settings.maintenanceMode ? '維護中' : '已完成'}</strong>
                  </div>
                </div>

                <div className="tl-settings-preview-state success">
                  <span>這筆資料已完成</span>
                  <strong>{settings.completedMessage}</strong>
                  <p>{settings.completedThankYou}</p>
                  <small>付款單：PA260618-70307171</small>
                </div>

                <div className="tl-settings-preview-state expired">
                  <span>換貨時間已截止</span>
                  <strong>{settings.expiredMessage}</strong>
                  <p>{settings.expiredThankYou}</p>
                  <small>截止時間：2026/06/30 23:59</small>
                </div>

                {settings.maintenanceMode && (
                  <div className="tl-settings-preview-state maintenance">
                    <span>{settings.maintenanceTitle}</span>
                    <strong>{settings.maintenanceMessage}</strong>
                    <p>客人目前不會看到商品選擇與送出按鈕。</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {showResetConfirm && (
          <div className="tl-settings-modal-overlay" role="presentation" onClick={() => setShowResetConfirm(false)}>
            <div className="tl-settings-modal" role="dialog" aria-modal="true" aria-labelledby="tl-settings-reset-title" onClick={(e) => e.stopPropagation()}>
              <h2 id="tl-settings-reset-title">恢復預設內容？</h2>
              <p>目前頁面上的文字、公告顏色與換貨參數會先恢復預設值，按下「儲存設定」後才會正式套用。</p>
              <div className="tl-settings-modal-actions">
                <button type="button" className="modern-btn modern-btn-secondary" onClick={() => setShowResetConfirm(false)}>
                  取消
                </button>
                <button type="button" className="modern-btn modern-btn-primary" onClick={resetToDefault}>
                  恢復預設
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TianlanSettings;
